import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { AlertCircle, Eye, EyeOff, CheckCircle, X, ChevronUp, ChevronDown } from 'lucide-react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /**
   * Etiqueta del input
   */
  label?: string;
  /**
   * Contenido adicional junto al label (tooltips, badges, botones auxiliares)
   */
  labelAction?: ReactNode;
  /**
   * Si el campo es requerido
   */
  required?: boolean;
  /**
   * Mensaje de error a mostrar
   */
  error?: string;
  /**
   * Oculta el texto del error (mantiene el borde rojo). Útil cuando el error se muestra fuera del input.
   */
  hideErrorMessage?: boolean;
  /**
   * Texto de ayuda debajo del input
   */
  helperText?: string;
  /**
   * Clases adicionales para el texto de ayuda (ej: 'text-success' para verde)
   */
  helperTextClassName?: string;
  /**
   * Icono a mostrar a la izquierda del input
   */
  leftIcon?: ReactNode;
  /**
   * Icono a mostrar a la derecha del input
   */
  rightIcon?: ReactNode;
  /**
   * Tamaño del input.
   * `auto` se adapta a la pantalla: `small` (32 px) por debajo de 1536 px y
   * `medium` (40 px) desde 1536 px. Pensado para barras de herramientas.
   */
  size?: 'small' | 'medium' | 'large' | 'auto';
  /**
   * Clases adicionales para el contenedor
   */
  containerClassName?: string;
  /**
   * ID único para accesibilidad
   */
  id?: string;
  /**
   * Variante de input validado
   */
  variant?: 'default' | 'validated-editable' | 'validated-readonly' | 'readonly' | 'number';
  /**
   * Paso de incremento/decremento para variant="number" (default: 1)
   */
  step?: number;
  /**
   * Valor mínimo para variant="number" (default: 0)
   */
  min?: number;
  /**
   * Valor máximo para variant="number"
   */
  max?: number;
  /**
   * Callback con el valor numérico ya parseado para variant="number"
   */
  onNumberChange?: (value: number) => void;
  /**
   * Mensaje de confirmación para variante validada
   */
  validationMessage?: {
    title: string;
    description?: string;
  };
  /**
   * Si se debe mostrar el mensaje de validación expandido
   */
  showValidationMessage?: boolean;
  /**
   * Callback cuando el usuario modifica el valor (para resetear validación)
   */
  onValueChange?: (value: string) => void;
  /**
   * Si se debe mostrar el botón de limpiar cuando hay contenido
   */
  showClearButton?: boolean;
  /**
   * Callback cuando se hace clic en el botón de limpiar
   */
  onClear?: () => void;
  /**
   * Prefijo dentro del input (ej. "S/", "%", "$", o contenido enriquecido como "PE +51")
   */
  prefix?: ReactNode;
}

/**
 * Componente Input reutilizable
 * 
 * Campo de entrada de datos con soporte para validaciones, iconos y diferentes estados.
 * Compatible con dark mode mediante CSS variables.
 * 
 * @example
 * ```tsx
 * // Input básico
 * <Input
 *   label="Nombre"
 *   placeholder="Ingrese su nombre"
 *   required
 *   error="Este campo es requerido"
 * />
 * 
 * // Input validado editable (RUC)
 * <Input
 *   label="RUC"
 *   value="20508997567"
 *   variant="validated-editable"
 *   validationMessage={{
 *     title: "RUC Validado Correctamente",
 *     description: "Datos completados desde SUNAT"
 *   }}
 *   showValidationMessage
 *   onValueChange={(value) => resetValidation()}
 * />
 * 
 * // Input validado readonly (Razón Social)
 * <Input
 *   label="Razón Social"
 *   value="CONTASIS S.A.C."
 *   variant="validated-readonly"
 *   helperText="Auto-completado desde SUNAT"
 *   readOnly
 * />
 * 
 * // Input readonly sin validación (ID, códigos)
 * <Input
 *   label="Código de Serie"
 *   value="F001"
 *   variant="readonly"
 *   readOnly
 * />
 * 
 * // Input con botón de limpiar
 * <Input
 *   label="Buscar"
 *   value={searchQuery}
 *   onChange={(e) => setSearchQuery(e.target.value)}
 *   showClearButton
 *   onClear={() => setSearchQuery('')}
 *   placeholder="Buscar..."
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  labelAction,
  required = false,
  error,
  hideErrorMessage = false,
  helperText,
  helperTextClassName,
  leftIcon,
  rightIcon,
  size: sizeProp = 'medium',
  containerClassName = '',
  className = '',
  type = 'text',
  id,
  disabled,
  variant = 'default',
  validationMessage,
  showValidationMessage = false,
  onValueChange,
  onChange,
  showClearButton = false,
  onClear,
  step,
  min,
  max,
  onNumberChange,
  prefix,
  style,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputId] = useState(() => id || `input-${Math.random().toString(36).substr(2, 9)}`);

  const isPassword  = type === 'password';
  const inputType   = isPassword && showPassword ? 'text' : type;
  const isValidated = variant === 'validated-editable' || variant === 'validated-readonly';
  const isReadonly  = variant === 'readonly';
  const isNumber    = variant === 'number';

  // ── Lógica variant="number" ────────────────────────────────────────────────
  const numericValue = parseFloat(String(props.value ?? 0)) || 0;
  const stepVal      = step ?? 1;
  const minVal       = min  ?? 0;
  const isAtMin      = numericValue <= minVal;
  const isAtMax      = max !== undefined && numericValue >= max;

  const handleIncrement = () => {
    if (disabled || isAtMax) return;
    const raw     = parseFloat((numericValue + stepVal).toFixed(10));
    const clamped = max !== undefined ? Math.min(raw, max) : raw;
    onNumberChange?.(clamped);
    onChange?.({ target: { value: String(clamped) } } as React.ChangeEvent<HTMLInputElement>);
    onValueChange?.(String(clamped));
  };

  const handleDecrement = () => {
    if (disabled || isAtMin) return;
    const raw     = parseFloat((numericValue - stepVal).toFixed(10));
    const clamped = Math.max(raw, minVal);
    onNumberChange?.(clamped);
    onChange?.({ target: { value: String(clamped) } } as React.ChangeEvent<HTMLInputElement>);
    onValueChange?.(String(clamped));
  };

  // `auto` solo cambia el alto y el texto (ver sizeClasses); el resto de medidas usa `medium`
  const size = sizeProp === 'auto' ? 'medium' : sizeProp;

  // ── Spinner sizes ──────────────────────────────────────────────────────────
  const spinnerWidth  = { small: 'w-7',  medium: 'w-8',  large: 'w-9'  };
  const spinnerRadius = { small: 'rounded-r-lg', medium: 'rounded-r-lg', large: 'rounded-r-lg' };
  const btnRadius     = {
    small:  { top: 'rounded-tr-lg', bottom: 'rounded-br-lg' },
    medium: { top: 'rounded-tr-lg', bottom: 'rounded-br-lg' },
    large:  { top: 'rounded-tr-lg', bottom: 'rounded-br-lg' },
  };
  const chevronSize   = { small: 'w-2.5 h-2.5', medium: 'w-3 h-3', large: 'w-3.5 h-3.5' };

  // ── Clear button ───────────────────────────────────────────────────────────
  const hasValue = Boolean(props.value || props.defaultValue);
  const shouldShowClearButton = showClearButton && hasValue && !disabled && !isValidated && !isPassword;

  // ── Clases para el tamaño ──────────────────────────────────────────────────
  const sizeClasses = {
    small:  'h-8  text-ui-sm  leading-8',
    medium: 'h-10 text-ui-base leading-10',
    large:  'h-12 text-body   leading-[3rem]',
    auto:   'h-8  text-ui-sm  leading-8 2xl:h-10 2xl:text-ui-base 2xl:leading-10',
  };

  // ── Prefix ────────────────────────────────────────────────────────────────
  const hasPrefix     = Boolean(prefix);
  const prefixPadX    = { small: 'px-2.5', medium: 'px-3', large: 'px-3.5' };
  const prefixRadius  = { small: 'rounded-l-lg', medium: 'rounded-l-lg', large: 'rounded-l-lg' };
  const prefixFont    = { small: 'text-ui-sm', medium: 'text-ui-sm', large: 'text-ui-base' };
  const prefixMinWidth = { small: 'min-w-[36px]', medium: 'min-w-[40px]', large: 'min-w-[44px]' };

  const prefixRef = useRef<HTMLDivElement>(null);
  const [prefixBoxWidth, setPrefixBoxWidth] = useState(0);

  useLayoutEffect(() => {
    if (prefixRef.current) setPrefixBoxWidth(prefixRef.current.offsetWidth);
  }, [prefix, size]);

  // ── Padding ────────────────────────────────────────────────────────────────
  const paddingLeftFallback = hasPrefix
    ? { small: 'pl-10', medium: 'pl-11', large: 'pl-12' }[size]
    : leftIcon ? 'pl-11' : 'px-4';
  const paddingLeft = hasPrefix ? '' : paddingLeftFallback;
  const paddingLeftStyle = hasPrefix && prefixBoxWidth
    ? { paddingLeft: `${prefixBoxWidth + 8}px` }
    : undefined;
  const paddingRight = isNumber
    ? { small: 'pr-8', medium: 'pr-9', large: 'pr-10' }[size]
    : isValidated || rightIcon || isPassword || shouldShowClearButton
      ? 'pr-11'
      : 'px-4';

  const labelMargin = size === 'small' ? 'mb-1' : 'mb-1.5';

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(e);
    if (onValueChange) onValueChange(e.target.value);
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' }, currentTarget: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    }
    if (onValueChange) onValueChange('');
  };
  
  return (
    <div className={containerClassName}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId}
          className={`block text-ui-sm font-medium ${labelMargin} text-tertiary`}
        >
          <span className="flex items-center gap-2">
            <span>
              {label} {required && <span className="text-error">*</span>}
            </span>
            {labelAction}
          </span>
        </label>
      )}
      
      {/* Input Container */}
      <div className="relative">
        {/* Prefix */}
        {hasPrefix && (
          <div
            ref={prefixRef}
            className={`absolute left-px top-px bottom-px flex items-center justify-center border-r border-strong bg-surface-1 ${prefixMinWidth[size]} ${prefixPadX[size]} ${prefixRadius[size]} ${disabled ? 'opacity-50' : ''}`}
          >
            <span className={`${prefixFont[size]} font-medium text-tertiary whitespace-nowrap`}>{prefix}</span>
          </div>
        )}

        {/* Left Icon */}
        {leftIcon && (
          <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${
            isValidated ? 'text-success' : 'text-tertiary'
          }`}>
            {leftIcon}
          </span>
        )}
        
        {/* Input */}
        <input
          ref={ref}
          id={inputId}
          name={props.name ?? inputId}
          type={inputType}
          disabled={disabled}
          onChange={handleChange}
          style={{ ...style, ...paddingLeftStyle }}
          className={`w-full ${sizeClasses[sizeProp]} ${paddingLeft} ${paddingRight} font-sans rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
            isValidated ?
              variant === 'validated-editable' ?
                'border-2 border-[var(--success-border)] bg-surface-0 cursor-text hover:border-[var(--success)] text-primary' :
                'border border-default bg-surface-2 cursor-default select-all text-secondary' :
            isReadonly ?
              'border border-default bg-surface-2 cursor-default select-all text-secondary' :
            error ? 'border border-error bg-surface-0 hover:border-strong text-primary' :
            'border border-strong bg-surface-0 hover:border-strong text-primary'
          } ${isNumber ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' : ''} ${className}`}
          aria-label={label || props['aria-label']}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${inputId}-error` : 
            (showValidationMessage && validationMessage) ? `${inputId}-validation` :
            helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />
        
        {/* Right: Validated Check, Password Toggle, Clear Button, or Custom Icon */}
        {isValidated ? (
          <CheckCircle 
            size={variant === 'validated-readonly' ? 18 : 20} 
            className="absolute right-3 top-1/2 -translate-y-1/2 text-success pointer-events-none" 
          />
        ) : isPassword ? (
          <button
            id={`${inputId}-toggle-password`}
            name={`${inputId}TogglePassword`}
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-secondary transition-colors"
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        ) : shouldShowClearButton ? (
          <button
            id={`${inputId}-clear`}
            name={`${inputId}Clear`}
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-secondary transition-colors"
            aria-label="Limpiar"
            title="Limpiar"
            tabIndex={-1}
          >
            <X size={18} />
          </button>
        ) : isNumber ? (
          <div className={`absolute right-0 top-0 bottom-0 flex flex-col border-l border-default overflow-hidden ${spinnerWidth[size]} ${spinnerRadius[size]} ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
            <button
              id={`${inputId}-increment`}
              name={`${inputId}Increment`}
              type="button"
              tabIndex={-1}
              onClick={handleIncrement}
              disabled={disabled || isAtMax}
              aria-label="Incrementar"
              className={`flex-1 flex items-center justify-center text-tertiary transition-colors duration-100 hover:bg-surface-1 hover:text-primary active:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed ${btnRadius[size].top}`}
            >
              <ChevronUp className={chevronSize[size]} strokeWidth={2.5} />
            </button>
            <div className="h-px bg-border-default flex-shrink-0" />
            <button
              id={`${inputId}-decrement`}
              name={`${inputId}Decrement`}
              type="button"
              tabIndex={-1}
              onClick={handleDecrement}
              disabled={disabled || isAtMin}
              aria-label="Decrementar"
              className={`flex-1 flex items-center justify-center text-tertiary transition-colors duration-100 hover:bg-surface-1 hover:text-primary active:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed ${btnRadius[size].bottom}`}
            >
              <ChevronDown className={chevronSize[size]} strokeWidth={2.5} />
            </button>
          </div>
        ) : rightIcon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary">
            {rightIcon}
          </span>
        ) : null}
      </div>
      
      {/* Error Message */}
      {error && !hideErrorMessage && (
        <div 
          className="flex items-center gap-1.5 text-caption text-error mt-2" 
          id={`${inputId}-error`}
        >
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      
      {/* Helper Text */}
      {!error && helperText && (
        <div
          className={`text-caption mt-2 ${helperTextClassName ?? 'text-tertiary'}`}
          id={`${inputId}-helper`}
        >
          {helperText}
        </div>
      )}
      
      {/* Mensaje de Validación Completo (Solo para validated-editable) */}
      {!error && variant === 'validated-editable' && showValidationMessage && validationMessage && (
        <div 
          className="mt-3 p-3 bg-success/10 border-l-4 border-success rounded" 
          id={`${inputId}-validation`}
        >
          <div className="flex items-start gap-2">
            <CheckCircle size={16} className="text-success flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-ui-sm font-semibold text-success mb-0.5">
                {validationMessage.title}
              </p>
              {validationMessage.description && (
                <p className="text-caption text-success/80">
                  {validationMessage.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';


